import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

// Actions
import { addFundToPortfolioById, addFundsToPortfolio, addStockToPortfolioById, setHoldingToReplace } from '@/redux/modules/edit-portfolio';
import { addSearchItemLog } from '@/redux/modules/activity-log';
import { fetchQuickSearchResults, loadRecentSearchItems, setFocusedQuickSearchEditPorfolio, QUICK_SEARCH_REDUCER_NAME } from '@/redux/modules/quick-search';
import { loadQuickAddItems, loadSearchFilters } from '@/redux/modules/advanced-search';
import {
  selectQuickSearchShelfStatusedResults,
  selectRecentSearchResults,
  selectSavedFundsShelfStatusedFund,
} from '@/redux/modules/shelf-management/selectors';

// Components
import CollapsibleContainer from '@/views/components/misc/CollapsibleContainer';
import EditPortfolioScreener from '@/views/components/edit-portfolio/EditPortfolioScreener';
import LoadingSpinner from '@/views/components/common/LoadingSpinner';
import QuickSearchV2Item, { QUICK_SEARCH_V2_ITEM } from './QuickSearchV2Item';
import { Button, SecondaryLink } from '@/views/components/buttons';
import { ControlItem, Tabs } from '@/views/components/navigation';
import { SearchInput } from '@/views/components/inputs';
import { SimulatorModal } from '@/views/components/modals';
import { Tooltip } from '@/views/components/tooltips';
import { withErrorBoundary } from '@/views/components/errors';
import Modal from '@/views/components/modals/AccessibleModal';

// Enums
import { ADVANCED_SEARCH_OPTIONS, COMPARISON_ITEM_TYPES, YieldParamsType } from '@/utils/constants';
import { INSTRUMENT_TYPE } from '@/enums/InstrumentByCode';
import { QUICK_SEARCH_FILTERS } from '@/enums/QuickSearch';
import { SEARCH_ITEM_DEST, SEARCH_ITEM_SOURCE } from '@/utils/activity-log';

// Libraries
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import PerfectScrollbar from 'react-perfect-scrollbar';

// Types
import ComparisonBenchmark from '@/types/comparison-benchmark';
import ComparisonItemType from '@/types/comparison-item-type';
import Fund from '@/types/fund';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import PortfolioHoldingType from '@/types/portfolio-holding-type';
import QuickAddGICSimulator from '@/types/quick-add-gic-simulator';
import QuickAddHISASimulator from '@/types/quick-add-hisa-simulator';
import QuickAddItems from '@/types/quick-add-items';
import QuickAddItemsList from '@/types/quick-add-items-list';
import QuickAddUIType from '@/types/quick-add-u-i-type';
import QuickSearchItem from '@/types/quick-search-item';
import RecentSearchItem from '@/types/recent-search-item';
import User from '@/types/user';
import { FundSearchState } from '@/redux/modules/types/fund-search';
import { ReduxState } from '@/redux/modules/types';

// Utils
import exchangeMapper from '@/utils/exchange-mapper';
import helpers from '@/utils/disclaimers/helpers';
import { filterIsShown, fundToQuickSearchItem, getPillLabel } from '@/utils/quick-search';
import { deepClone } from '@/utils/objects';
import { formatLocalizedString, formatMessage, FormattedMessage, LOCALE } from '@/utils/i18n';
import { MODAL_SIZES } from '@/utils/constants';
import { showError } from '@/utils/errors';

type ALL_SECTION = {
  i18nKey: string;
  value: string;
  getOptions: () => QUICK_SEARCH_V2_ITEM[];
};

enum QUICK_SEARCH_ALL_SECTIONS {
  QUICK_ADD = 'quickAdd',
  RECENT_SEARCH = 'recentSearch',
  SIMULATION = 'simulation',
}

type DispatchProps = {
  addFundToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
  addFundsToPortfolio: (funds: Fund[]) => Promise<void[]>;
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  addStockToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
  fetchQuickSearchResults: (
    query: string,
    reducerName: string,
    types?: QUICK_SEARCH_FILTERS[],
    excludeSubtypes?: boolean,
    limit?: number,
    universe?: string,
  ) => Promise<void>;
  loadQuickAddItems: () => Promise<void>;
  loadRecentSearchItems: (reducerName: string) => Promise<void>;
  loadSearchFilters: (overrideLocale: Locale) => Promise<void>;
  setFocusedQuickSearchEditPorfolio: (focused?: boolean) => void;
  setHoldingToReplace: (holdingToReplace?: PortfolioHolding) => void;
};

type StateProps = {
  fundState: FundSearchState;
  holdingToReplace?: PortfolioHolding;
  myBenchmarks: ComparisonBenchmark[];
  myFunds: Fund[];
  portfolio: Portfolio;
  quickAddItems: QuickAddItems[];
  isFocusedQuickSearch?: boolean;
  recentSearchItems: RecentSearchItem[];
  results: QuickSearchItem[];
  user?: Nullable<User>;
};

type LocalProps = {
  targetReducer: QUICK_SEARCH_REDUCER_NAME;
};

type Props = LocalProps &
  DispatchProps &
  StateProps & {
    holdingToReplace?: PortfolioHolding;
    onAddBenchmarkById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
    onAddFundById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
    onAddStockById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
    onSetFocusedItem: (focused?: boolean) => void;
    onOpenScreenerModal?: (open: boolean) => void;
    portfolio?: Portfolio;
  };

const LegacyQuickSearchV2 = (props: Props) => {
  const {
    fundState: { selectedFunds },
    fundState: { filtersData: fundFilters },
    holdingToReplace,
    isFocusedQuickSearch,
    myFunds,
    portfolio,
    quickAddItems,
    // recentSearchItems,
    results,
    user,
    addFundToPortfolioById,
    addFundsToPortfolio,
    addSearchItemLog,
    addStockToPortfolioById,
    fetchQuickSearchResults,
    loadQuickAddItems,
    loadRecentSearchItems,
    loadSearchFilters,
    setFocusedQuickSearchEditPorfolio,
    setHoldingToReplace,
  } = props;

  const itemWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<SearchInput>(null);

  const [favoriteItems, setFavoriteItems] = useState<QuickSearchItem[]>([]);
  const [simulationItems, setSimulationItems] = useState<QUICK_SEARCH_V2_ITEM[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<QUICK_SEARCH_FILTERS>(QUICK_SEARCH_FILTERS.ALL);
  const [showGicModal, setShowGicModal] = useState<boolean>(false);
  const [showHisaModal, setShowHisaModal] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [showScreenerModal, setShowScreenerModal] = useState<boolean>(false);

  const allSearchableOptions = [QUICK_SEARCH_FILTERS.FUNDS, QUICK_SEARCH_FILTERS.STOCKS, QUICK_SEARCH_FILTERS.BONDS, QUICK_SEARCH_FILTERS.BENCHMARK_PROXIES];
  const debounceRate = 300;
  const i18nBasePath = 'Search.QuickSearchV2';
  const maxQueryLimit = 100;
  const queryLimit = 25;
  const resultsLimit = 50;

  const fetchSearchResults = (filter: Nullable<QUICK_SEARCH_FILTERS>, query: string): Promise<void> => {
    const isSMAFilter = filter === QUICK_SEARCH_FILTERS.SMA;
    const isSpecificFilter = filter && filter !== QUICK_SEARCH_FILTERS.ALL && !isSMAFilter;
    const limit = isSpecificFilter ? queryLimit : maxQueryLimit;
    const types = isSpecificFilter ? [filter] : allSearchableOptions;
    const universe = isSMAFilter ? QUICK_SEARCH_FILTERS.SMA : undefined;

    return fetchQuickSearchResults(query, QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO, types, false, limit, universe);
  };

  const debouncedFetchQuickSearchResults = useRef(
    debounce((filter: Nullable<QUICK_SEARCH_FILTERS>, query: string) => {
      Promise.resolve(fetchSearchResults(filter, query)).finally(() => setIsLoading(false));
    }, debounceRate),
  ).current;

  // Load the fund filters to display the good filter tabs
  useEffect(() => {
    // Note : we force the locale to be en_CA because we only need to know which filters are available
    // for this user and it is easier to compare when we fix the locale.
    // At the moment it is perfect because we do not need to display them.
    // If you need to display the filters, hence need to get them in the user's language,
    // you must make sure that the function filterIsShown still works properly.
    loadSearchFilters(LOCALE.EN_CA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = ({ target }: MouseEvent) => {
      if (itemWrapperRef.current && !itemWrapperRef.current.contains(target as Node)) {
        setShowResults(false);
        clearSearch();
        setSelectedFilter(QUICK_SEARCH_FILTERS.ALL);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFocusedQuickSearch) {
      searchInputRef.current?.focusInput();
    }
  }, [isFocusedQuickSearch]);

  useEffect(() => {
    loadQuickAddItems();

    // TODO: implement and test the recent searches from CONN-6033
    // loadRecentSearchItems(QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO);
  }, [loadQuickAddItems, loadRecentSearchItems]);

  const clearSearch = (focusInput?: boolean) => {
    if (focusInput) {
      searchInputRef.current?.focusInput();
    }

    if (selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE) {
      setFavoriteItems(getFavoriteItems());
    }

    if (selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION) {
      setSimulationItems(getSimulationItems());
    }

    return setSearchTerm('');
  };

  const closeResults = () => {
    setSearchTerm('');
    setSelectedFilter(QUICK_SEARCH_FILTERS.ALL);
    setShowResults(false);
  };

  const getAllSections = () => [
    {
      i18nKey: `${i18nBasePath}.AllSections.RecentSearch`,
      value: QUICK_SEARCH_ALL_SECTIONS.RECENT_SEARCH,
      getOptions: () => setRecentSearchOptions(),
    },
    {
      i18nKey: `${i18nBasePath}.AllSections.QuickAdd`,
      value: QUICK_SEARCH_ALL_SECTIONS.QUICK_ADD,
      getOptions: () => setQuickAddOptions(),
    },
    {
      i18nKey: `${i18nBasePath}.AllSections.Simulation`,
      value: QUICK_SEARCH_ALL_SECTIONS.SIMULATION,
      getOptions: () => getSimulationItems(),
    },
  ];

  const getFavoriteItems = (filter?: QUICK_SEARCH_FILTERS) => {
    const funds = myFunds.map(f => fundToQuickSearchItem(f));
    return funds;
  };

  const getFundScreenerActions = () => [
    <Button compact icon="plus" key="action-add-to-portfolio" onClick={onAddFundsToPortfolio}>
      <FormattedMessage id="Search.Funds.AddToPortfolio" />
    </Button>,
  ];

  const getSearchPlaceholder = () => {
    switch (selectedFilter) {
      case QUICK_SEARCH_FILTERS.FAVORITE:
        return formatMessage(`${i18nBasePath}.SearchFavorites`);
      case QUICK_SEARCH_FILTERS.SIMULATION:
        return formatMessage(`${i18nBasePath}.SearchSimulations`);
      default:
        return formatMessage(`${i18nBasePath}.SearchHoldings`);
    }
  };

  const getSimulationItems = () => {
    const itemsTypeGic = quickAddItems.filter((item): item is QuickAddGICSimulator => item.uiType === QuickAddUIType.GICSimulator);
    const itemsTypeHisa = quickAddItems.filter((item): item is QuickAddHISASimulator => item.uiType === QuickAddUIType.HISASimulator);
    const gicItems: QUICK_SEARCH_V2_ITEM[] = [];
    const hisaItems: QUICK_SEARCH_V2_ITEM[] = [];

    itemsTypeGic.forEach((item: QuickAddGICSimulator) => {
      gicItems.push({
        code: null,
        id: item.key,
        lineItemAddLabel: formatMessage(`${i18nBasePath}.AddSimulation`),
        name: formatMessage(`${i18nBasePath}.GICSimulator`),
        pillLabel: item.key,
      } as QUICK_SEARCH_V2_ITEM);
    });

    itemsTypeHisa.forEach((item: QuickAddHISASimulator) => {
      hisaItems.push({
        code: null,
        id: item.key,
        lineItemAddLabel: formatMessage(`${i18nBasePath}.AddSimulation`),
        name: formatMessage(`${i18nBasePath}.HISASimulator`),
        pillLabel: item.key,
      } as QUICK_SEARCH_V2_ITEM);
    });

    return [...gicItems, ...hisaItems];
  };

  const getTabsOptions = () => {
    const tabs: { [key in Exclude<QUICK_SEARCH_FILTERS, QUICK_SEARCH_FILTERS.PROXIES>]: string } = {
      [QUICK_SEARCH_FILTERS.ALL]: `${i18nBasePath}.Filters.All`,
      [QUICK_SEARCH_FILTERS.FUNDS]: `${i18nBasePath}.Filters.Funds`,
      [QUICK_SEARCH_FILTERS.STOCKS]: `${i18nBasePath}.Filters.Stocks`,
      [QUICK_SEARCH_FILTERS.BONDS]: `${i18nBasePath}.Filters.Bonds`,
      [QUICK_SEARCH_FILTERS.SMA]: `${i18nBasePath}.Filters.SMA`,
      [QUICK_SEARCH_FILTERS.BENCHMARK_PROXIES]: `${i18nBasePath}.Filters.Benchmarks`,
      [QUICK_SEARCH_FILTERS.BENCHMARKS]: '', // Don't show Benchmarks until we start using this for Comparison
      [QUICK_SEARCH_FILTERS.ALTS]: `${i18nBasePath}.Filters.Alts`,
      [QUICK_SEARCH_FILTERS.SIMULATION]: `${i18nBasePath}.Filters.Simulation`,
      [QUICK_SEARCH_FILTERS.FAVORITE]: `${i18nBasePath}.Filters.Favorite`,
    };
    return Object.keys(tabs)
      .map(key => ({
        value: key as QUICK_SEARCH_FILTERS,
        i18nKey: tabs[key as Exclude<QUICK_SEARCH_FILTERS, QUICK_SEARCH_FILTERS.PROXIES>],
        hidden: !filterIsShown(key as QUICK_SEARCH_FILTERS, user, fundFilters),
      }))
      .filter(i => !i.hidden);
  };

  const handleAddedSearchItem = (id: string, type: PortfolioHoldingType | ComparisonItemType) => {
    addSearchItemLog(id, type, SEARCH_ITEM_SOURCE.QUICKSEARCH, SEARCH_ITEM_DEST.PORTFOLIO);
    handleOnReplacingItem(true, true);
    // TODO: implement and test the recent searches from CONN-6033
    // loadRecentSearchItems(QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO);
  };

  const handleGetResults = (filter: QUICK_SEARCH_FILTERS, query: string) => {
    debouncedFetchQuickSearchResults.cancel();

    if (!query) {
      return;
    }

    if (!isLoading) {
      setIsLoading(true);
    }

    // the favorite filter only filters the saved items by the user
    if (filter === QUICK_SEARCH_FILTERS.FAVORITE) {
      const items = getFavoriteItems(filter);

      if (query) {
        const filteredItems = items.filter(item => {
          const term = query.toLocaleLowerCase();
          const itemSymbol = item.symbol ? item.symbol?.toLocaleLowerCase()?.includes(term) : false;
          const localItemName = formatLocalizedString(item.name).toLocaleLowerCase()?.includes(term);
          return localItemName || itemSymbol;
        });
        setFavoriteItems(filteredItems);
      } else {
        setFavoriteItems(items);
      }

      setIsLoading(false);
      return;
    }

    // the simulation filter only filters the items type GIC or HISA
    if (filter === QUICK_SEARCH_FILTERS.SIMULATION) {
      const items = getSimulationItems();

      if (query) {
        const filteredItems = items.filter(item => {
          const term = query.toLocaleLowerCase();
          const pillLabel = item.pillLabel ? item.pillLabel?.toLocaleLowerCase()?.includes(term) : false;
          const localItemName = item.name.toLocaleLowerCase()?.includes(term);
          return localItemName || pillLabel;
        });
        setSimulationItems(filteredItems);
      } else {
        setSimulationItems(items);
      }

      setIsLoading(false);
      return;
    }

    debouncedFetchQuickSearchResults(filter, query);
  };

  const handleOnReplacingItem = (clearHolding = true, clearResults = false) => {
    if (holdingToReplace && clearHolding) {
      setHoldingToReplace(undefined);
    }

    if (holdingToReplace && clearResults) {
      closeResults();
    }

    if (holdingToReplace) {
      searchInputRef.current?.blurInput();
      setFocusedQuickSearchEditPorfolio(false);
    }
  };

  const onAddFundsToPortfolio = () => {
    addFundsToPortfolio(selectedFunds).then(() => setShowScreenerModal(false));
  };

  const onAddItem = (item: QUICK_SEARCH_V2_ITEM) => {
    const { id, type } = item;

    if (!item || !id) {
      return;
    }

    if (id === YieldParamsType.GIC) {
      setShowGicModal(true);
      closeResults();
      return;
    }

    if (id === YieldParamsType.HISA) {
      setShowHisaModal(true);
      closeResults();
      return;
    }

    switch (type) {
      case COMPARISON_ITEM_TYPES.BENCHMARK:
      case COMPARISON_ITEM_TYPES.FUND: {
        return addFundToPortfolioById(id, holdingToReplace, true)
          .then(() => handleAddedSearchItem(id, type))
          .catch(showError);
      }
      case COMPARISON_ITEM_TYPES.STOCK: {
        return addStockToPortfolioById(id, holdingToReplace, true)
          .then(() => handleAddedSearchItem(id, type))
          .catch(showError);
      }
      default: {
        return Promise.reject(new Error('Unsupported search item type'));
      }
    }
  };

  const onAddToPortfolio = (fund: Fund) => {
    addFundToPortfolioById(fund._id, holdingToReplace, true)
      .then(() => {
        setShowScreenerModal(false);
        return handleAddedSearchItem(fund._id, COMPARISON_ITEM_TYPES.FUND);
      })
      .catch(showError);
  };

  const onChangeFilter = (filter: QUICK_SEARCH_FILTERS) => {
    if (selectedFilter === filter) {
      return;
    }

    setSelectedFilter(filter);

    if (!searchTerm && filter === QUICK_SEARCH_FILTERS.FAVORITE) {
      setFavoriteItems(getFavoriteItems(filter));
    }

    if (!searchTerm && filter === QUICK_SEARCH_FILTERS.SIMULATION) {
      setSimulationItems(getSimulationItems());
    }

    handleGetResults(filter, searchTerm);
  };

  const onSearch = (query: string) => {
    if (!query) {
      return clearSearch();
    }

    setSearchTerm(query);
    handleGetResults(selectedFilter, query);
  };

  const openScreenerModal = () => {
    setShowScreenerModal(true);
    setShowResults(false);
  };

  const renderAllSections = () => {
    const allSections = getAllSections().reduce((acc: ALL_SECTION[], cur: ALL_SECTION) => {
      const curOptions = cur.getOptions();
      if (cur && curOptions.length) acc.push(cur);
      return acc;
    }, []);

    return allSections.map((section: ALL_SECTION) => (
      <div key={section.value} data-testid={`all-section-${section.value}`}>
        <CollapsibleContainer defaultOpen headerText={formatMessage(section.i18nKey)} icon="arrow-drop-down">
          {section.getOptions().map(item => renderItem(item, section.value))}
        </CollapsibleContainer>
      </div>
    ));
  };

  const renderEmptyResults = () => {
    if (isLoading) {
      return null;
    }

    const isFavoriteFilter = selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE;
    const isSimulationFilter = selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION;
    const isItemFilter = selectedFilter !== QUICK_SEARCH_FILTERS.ALL && !isFavoriteFilter && !isSimulationFilter;

    if (
      searchTerm &&
      ((isFavoriteFilter && !favoriteItems.length) || (isSimulationFilter && !simulationItems.length) || (!results.length && !isFavoriteFilter))
    ) {
      return (
        <div className="no-results-message" data-testid="no-results-found">
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoResultsFound`} tagName="h3" />
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NotMatch`} tagName="p" values={{ searchTerm }} />
          <SecondaryLink className="secondary-link-v2" onClick={() => clearSearch(true)}>
            <FormattedMessage id={`${i18nBasePath}.EmptyResults.ClearSearch`} />
          </SecondaryLink>
        </div>
      );
    }

    if (!searchTerm && isFavoriteFilter && !favoriteItems.length) {
      return (
        <div className="no-results-message" data-testid="no-favorite-added">
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoFavoriteAdded`} tagName="h3" />
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.SearchOtherTabs`} tagName="p" />
        </div>
      );
    }

    if (!searchTerm && isItemFilter) {
      return (
        <div className="no-results-message" data-testid="no-search-history">
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoSearchHistory`} tagName="h3" />
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.BeginYourSearch`} tagName="p" />
        </div>
      );
    }

    return null;
  };

  const renderFavoriteResults = () => favoriteItems.map(item => renderItem(formatToQuickSearchItem(item), QUICK_SEARCH_FILTERS.FAVORITE));

  const renderItem = (item: QUICK_SEARCH_V2_ITEM, key: string) => {
    const { id } = item;
    return <QuickSearchV2Item isAddedItem={validateIfItemIsInPortfolio(id)} item={item} key={`${key}-${id}`} onAddItem={onAddItem} />;
  };

  const renderReplaceProductTooltip = (name: LocalizedString) => (
    <div className="replace-tooltip">
      <Tooltip
        className="custom-tooltip-v2"
        title={formatMessage(`${i18nBasePath}.ReplaceTooltip.Title`)}
        message={formatMessage(`${i18nBasePath}.ReplaceTooltip.Message`, {
          product: helpers.getTrimmedHoldingName(formatLocalizedString(name)),
        })}
        place="left"
      >
        <span />
      </Tooltip>
    </div>
  );

  const renderSimulationResults = () => simulationItems.map(item => renderItem(item, QUICK_SEARCH_FILTERS.SIMULATION));

  const renderOpenScreener = () => (
    <>
      <FormattedMessage id={`${i18nBasePath}.CanNotFind`} />
      <SecondaryLink className="secondary-link-v2" onClick={openScreenerModal}>
        <i className="icon-expand-top-right" />
        <FormattedMessage id={`${i18nBasePath}.OpenScreener`} />
      </SecondaryLink>
    </>
  );

  const renderResults = () => {
    if (!searchTerm && selectedFilter === QUICK_SEARCH_FILTERS.ALL) {
      return renderAllSections();
    }

    if (searchTerm && selectedFilter !== QUICK_SEARCH_FILTERS.FAVORITE && selectedFilter !== QUICK_SEARCH_FILTERS.SIMULATION) {
      return renderSearchResults();
    }

    if (selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE) {
      return renderFavoriteResults();
    }

    if (selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION) {
      return renderSimulationResults();
    }

    return <></>;
  };

  const renderResultsCounter = (filter: QUICK_SEARCH_FILTERS) => {
    const isFavoriteFilter = selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE;
    const isFavoriteSearchEmpty = !searchTerm && isFavoriteFilter;
    const isSimulationFilter = selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION;
    const isSimulationSearchEmpty = !searchTerm && isSimulationFilter;
    let counter: number | string = 0;

    // when the results are more than the resultsLimit (50), the counter should only display '50+'
    if (results.length >= resultsLimit) {
      counter = `${resultsLimit}+`;
    } else {
      counter = results.length;
    }

    if (isFavoriteFilter) {
      counter = favoriteItems.length;
    }

    if (isSimulationFilter) {
      counter = simulationItems.length;
    }

    if (filter === selectedFilter && (searchTerm || isFavoriteSearchEmpty || isSimulationSearchEmpty)) {
      return <span className="counter">{isLoading ? <LoadingSpinner /> : counter}</span>;
    }

    return null;
  };

  const renderSearchResults = () => {
    const favoriteItems = getFavoriteItems();
    const commonItems = favoriteItems.filter(fi => results.some(r => r._id === fi._id));
    const groupedResults = commonItems.concat(results.filter(r => !commonItems.some(si => si._id === r._id)));
    return groupedResults.map(item => renderItem(formatToQuickSearchItem(item), selectedFilter));
  };

  // this returns an object type QUICK_SEARCH_V2_ITEM to render the item
  // based on a item type of QuickSearchItem
  const formatToQuickSearchItem = (item: QuickSearchItem) => {
    const clonedQuickSearchItem = deepClone(item);
    const { _id, flags, name, saved, symbol, type } = clonedQuickSearchItem;
    const exchange: string | undefined = exchangeMapper(clonedQuickSearchItem);
    const code = exchange != null ? `${symbol}:${exchange}` : symbol;

    return {
      code,
      flags,
      id: _id,
      isFavorite: saved,
      lineItemAddLabel: formatMessage(`${i18nBasePath}.AddToPortfolio`),
      name: formatLocalizedString(name),
      pillLabel: getPillLabel(item),
      type,
    } as QUICK_SEARCH_V2_ITEM;
  };

  // this method formats the Cash & Proxies information to the Quick Add section
  // from redux we could receive items type: QuickAddItemsList | QuickAddGICSimulator | QuickAddHISASimulator
  const setQuickAddOptions = () => {
    const itemsTypeList: QuickAddItemsList[] = quickAddItems.filter(item => item.uiType === QuickAddUIType.ItemList).reverse() as QuickAddItemsList[];
    const cashItems: QUICK_SEARCH_V2_ITEM[] = [];
    const proxyItems: QUICK_SEARCH_V2_ITEM[] = [];

    // here we need to iterate only the filtered items with uiType 'ITEM_LIST'
    itemsTypeList.forEach((item: QuickAddItemsList) => {
      item.options.items?.forEach(optionsItem => {
        const { code, displayName, refId, type } = optionsItem;
        cashItems.push({
          code,
          id: refId,
          isProxy: true,
          lineItemAddLabel: formatMessage(`${i18nBasePath}.AddToPortfolio`),
          name: formatLocalizedString(displayName),
          pillLabel: INSTRUMENT_TYPE.CASH,
          type,
        });
      });

      item.options.subSections?.forEach(subSection => {
        subSection.items?.forEach(subSectionItem => {
          const { code, displayName, refId, type } = subSectionItem;
          proxyItems.push({
            code,
            id: refId,
            isProxy: true,
            lineItemAddLabel: formatMessage(`${i18nBasePath}.AddToPortfolio`),
            name: formatLocalizedString(displayName),
            pillLabel: INSTRUMENT_TYPE.PROXY,
            type,
          });
        });
      });
    });

    // we only return the Cash and Proxy items that were added in the itemsTypeList iteration
    return [...cashItems, ...proxyItems];
  };

  const setRecentSearchOptions = () =>
    // TODO: return the recentSearchItems when the endpoint will be ready
    // return recentSearchItems || [];
    [] as QUICK_SEARCH_V2_ITEM[];
  const validateIfItemIsInPortfolio = (id: string) => {
    const { holdings } = portfolio;
    return !!id && holdings.some(h => h.original.id === id);
  };

  return (
    <>
      <div className={classNames('quick-search-v2', { 'is-searching': showResults })} ref={itemWrapperRef}>
        {holdingToReplace && showResults && renderReplaceProductTooltip(holdingToReplace.name)}

        <SearchInput
          className="search-input-v2"
          onChange={onSearch}
          onFocus={() => setShowResults(true)}
          onBlur={() => !showResults && handleOnReplacingItem(false)}
          placeholder={getSearchPlaceholder()}
          ref={searchInputRef}
          rightSection={searchTerm ? <i className="icon-cross-thin clear-term" onClick={() => clearSearch()} /> : undefined}
          value={searchTerm}
        />

        {showResults && (
          <div className="results" data-testid="results">
            <Tabs className="tabs-v2" onChange={onChangeFilter} selected={selectedFilter}>
              {getTabsOptions().map(({ value, i18nKey }) => (
                <ControlItem data-testid={`filter-${value}`} key={value} value={value}>
                  <FormattedMessage id={i18nKey} />
                  {renderResultsCounter(value)}
                </ControlItem>
              ))}
            </Tabs>

            <div className="results-list">
              {isLoading ? <LoadingSpinner /> : <PerfectScrollbar options={{ suppressScrollX: true }}>{renderResults()}</PerfectScrollbar>}
            </div>

            <div className="no-results">{renderEmptyResults()}</div>

            <div className="results-open-screener">{renderOpenScreener()}</div>
          </div>
        )}
      </div>

      {showGicModal && (
        <SimulatorModal
          onCancel={() => {
            setShowGicModal(false);
            handleOnReplacingItem();
          }}
          onConfirm={() => setShowGicModal(false)}
          show={showGicModal}
          simulatorTitle={formatMessage(`${i18nBasePath}.GICSimulator`)}
          simulatorType={YieldParamsType.GIC}
        />
      )}

      {showHisaModal && (
        <SimulatorModal
          onCancel={() => {
            setShowHisaModal(false);
            handleOnReplacingItem();
          }}
          onConfirm={() => setShowHisaModal(false)}
          show={showHisaModal}
          simulatorTitle={formatMessage(`${i18nBasePath}.HISASimulator`)}
          simulatorType={YieldParamsType.HISA}
        />
      )}

      {showScreenerModal && (
        <Modal
          backdropClassName="modal-v2-backdrop"
          className={classNames('screener-modal')}
          onHide={() => {
            setShowScreenerModal(false);
            handleOnReplacingItem();
          }}
          show={showScreenerModal}
          size={MODAL_SIZES.XXL}
        >
          <Modal.Body className="d-flex flex-column">
            <EditPortfolioScreener
              customTableActions={getFundScreenerActions()}
              hideExpandButton
              isExpanded={!holdingToReplace}
              showInlineAdd={!!holdingToReplace}
              onAddItem={onAddToPortfolio}
              onClose={() => {
                setShowScreenerModal(false);
                handleOnReplacingItem();
              }}
              selectedOption={ADVANCED_SEARCH_OPTIONS.FUNDS}
              title={formatMessage('EditPortfolioModal.FundScreener')}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  fundState: state.advancedSearch.fund,
  holdingToReplace: state.editPortfolio.holdingToReplace,
  isFocusedQuickSearch: state.quickSearchEditPortfolio.focused,
  myBenchmarks: state.comparisonBenchmarks.savedBenchmarks.benchmarks,
  myFunds: selectSavedFundsShelfStatusedFund(state.funds.savedFunds).funds,
  portfolio: state.editPortfolio.portfolio,
  quickAddItems: state.advancedSearch.quickAddItems,
  recentSearchItems: selectRecentSearchResults(state.quickSearchEditPortfolio).recentSearchItems,
  results: selectQuickSearchShelfStatusedResults(state.quickSearchEditPortfolio).results,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  addFundToPortfolioById,
  addFundsToPortfolio,
  addSearchItemLog,
  addStockToPortfolioById,
  fetchQuickSearchResults,
  loadQuickAddItems,
  loadRecentSearchItems,
  loadSearchFilters,
  setFocusedQuickSearchEditPorfolio,
  setHoldingToReplace,
})(withErrorBoundary(LegacyQuickSearchV2));
